import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { DataService } from "./data.service";
import { MedicalOrderScann } from "../model/medical-order-scann";
import { MedicalOrderScannType } from "../model/medical-order-scann-type";
import { MedicalOrderScannRequest } from "../model/medical-order-scann-request";
import { MedicalOrderScannUpdateResponse } from "../model/medical-order-scann/medical-order-scann-update-response";
import { Admission } from "../model/admission";
import { MedicalOrderEditInfo } from "../model/medical-order-edit-info";
import { ValorizationEdit } from "../model/valorization-edit";
import { MedicalAppointment } from "../model/medical-appointment";
import { MedicalOrder } from "../model/medical-order";
import { MedicalOrderScannInfo } from "../model/medical-order-scann-info";
import { PracticeRegion } from "../model/practice-region";
import { PracticeRegionFilter } from "../model/practice-region-filter";
import {
  MedicalOrderExtracted,
  MedicalOrderProcedureExtraction,
} from "../model/medical-order-procedure-extraction/medical-order-procedure-extraction";

@Injectable()
export class MedicalOrderService {
  constructor(private dataService: DataService) {}

  private readonly medicalOrderScannRotateEndPoint: string =
    "/medicalOrder/medicalOrderScannRotate/";
  private readonly medicalOrderScannUpdateEndPoint: string =
    "/medicalOrder/medicalOrderScannUpdate/";
  private readonly medicalOrderScannDeleteEndPoint: string =
    "/medicalOrder/medicalOrderScannDelete/";
  private readonly createMedicalOrderScannFromTemporaryStorageEndPoint: string =
    "/medicalOrder/createMedicalOrderScannFromTemporaryStorage/";
  private readonly getMedicalOrderScannTypesEndPoint: string =
    "/medicalOrder/getMedicalOrderScannTypes/";
  private readonly getMedicalOrderScannEndPoint: string =
    "/medicalOrder/getMedicalOrderScann/";
  private readonly getMedicalsOrderScannEndPoint: string =
    "/medicalOrder/getMedicalsOrderScann/";
  private readonly getMedicalOrderScannInfoEndPoint: string =
    "/medicalOrder/getMedicalOrderScannInfo/";
  private readonly medicalOrderEditInfoForAdmissionEndPoint: string =
    "/medicalOrder/medicalOrderEditInfoForAdmission/";
  private readonly medicalOrderEditInfoForBillingEndPoint: string =
    "/medicalOrder/medicalOrderEditInfoForBilling/";
  private readonly getMedicalOrderEditInfoForBillingEndPoint: string =
    "/medicalOrder/getMedicalOrderEditInfoForBilling/";
  private readonly extractProceduresEndPoint: string =
    "/medicalOrder/extractProcedures/";

  async getMedicalOrderEditInfoForBilling(
    medicalAppointmentId: number
  ): Promise<MedicalOrderEditInfo> {
    const request = { medicalAppointmentId: medicalAppointmentId };
    return await this.dataService
      .get<MedicalOrderEditInfo>(
        this.getMedicalOrderEditInfoForBillingEndPoint,
        request
      )
      .toPromise();
  }

  async medicalOrderEditInfoForBilling(
    valorizationEdit: ValorizationEdit,
    medicalAppointment: MedicalAppointment,
    medicalOrder: MedicalOrder,
    medicalOrderEditInfo: MedicalOrderEditInfo
  ): Promise<MedicalOrderEditInfo> {
    const request = {
      ValorizationEdit: valorizationEdit,
      MedicalAppointment: medicalAppointment,
      MedicalOrder: medicalOrder,
      MedicalOrderEditInfo: medicalOrderEditInfo,
    };

    return await this.dataService
      .post<MedicalOrderEditInfo>(
        this.medicalOrderEditInfoForBillingEndPoint,
        request
      )
      .toPromise();
  }

  async medicalOrderEditInfoForAdmission(
    admission: Admission
  ): Promise<MedicalOrderEditInfo> {
    const request = {
      ValorizationEdit: admission.AdmissionValorization.ValorizationEdit,
      MedicalAppointment: admission.MedicalAppointment,
      MedicalOrder:
        admission.AdmissionMedicalOrder.MedicalOrderEdit.MedicalOrder,
      MedicalOrderEditInfo:
        admission.AdmissionMedicalOrder.MedicalOrderEdit.MedicalOrderEditInfo,
    };

    return await this.dataService
      .post<MedicalOrderEditInfo>(
        this.medicalOrderEditInfoForAdmissionEndPoint,
        request
      )
      .toPromise();
  }

  async getMedicalsOrderScann(
    medicalOrderId: number
  ): Promise<MedicalOrderScann[]> {
    const params = { medicalOrderId: medicalOrderId };
    return await this.dataService
      .get<MedicalOrderScann[]>(this.getMedicalsOrderScannEndPoint, params)
      .toPromise();
  }

  async getMedicalOrderScannInfo(
    medicalOrderId: number
  ): Promise<MedicalOrderScannInfo> {
    const params = {
      medicalOrderId: medicalOrderId,
    };

    return await this.dataService
      .get<MedicalOrderScannInfo>(this.getMedicalOrderScannInfoEndPoint, params)
      .toPromise();
  }

  async getMedicalOrderScann(
    medicalOrderScannId: number
  ): Promise<MedicalOrderScann> {
    const params = { medicalOrderScannId: medicalOrderScannId };
    return await this.dataService
      .get<MedicalOrderScann>(this.getMedicalOrderScannEndPoint, params)
      .toPromise();
  }

  async getMedicalOrderScannTypes(): Promise<MedicalOrderScannType[]> {
    const params = {};
    return await this.dataService
      .get<MedicalOrderScannType[]>(
        this.getMedicalOrderScannTypesEndPoint,
        params
      )
      .toPromise();
  }

  async medicalOrderScannUpdate(
    medicalsOrderScann: MedicalOrderScann[]
  ): Promise<MedicalOrderScannUpdateResponse[]> {
    return await this.dataService
      .post<MedicalOrderScannUpdateResponse[]>(
        this.medicalOrderScannUpdateEndPoint,
        medicalsOrderScann
      )
      .toPromise();
  }

  async medicalOrderScannRotate(
    medicalOrderScann: MedicalOrderScann
  ): Promise<string> {
    return await this.dataService
      .post<string>(this.medicalOrderScannRotateEndPoint, medicalOrderScann)
      .toPromise();
  }

  async medicalOrderScannDelete(
    medicalOrderScann: MedicalOrderScann
  ): Promise<boolean> {
    return await this.dataService
      .post<boolean>(this.medicalOrderScannDeleteEndPoint, medicalOrderScann)
      .toPromise();
  }

  async createMedicalOrderScannFromTemporaryStorage(
    medicalOrderScannRequest: MedicalOrderScannRequest
  ): Promise<MedicalOrderScann> {
    return await this.dataService
      .post<MedicalOrderScann>(
        this.createMedicalOrderScannFromTemporaryStorageEndPoint,
        medicalOrderScannRequest
      )
      .toPromise();
  }

  async extractProcedures(
    fileBase64: string,
    planId: number,
    centerId?: number,
    physicianId?: number,
    equipmentId?: number
  ): Promise<MedicalOrderExtracted> {
    const params = {
      fileBase64: fileBase64,
      planId: planId,
      centerId: centerId,
      physicianId: physicianId,
      equipmentId: equipmentId,
    };
    return await this.dataService
      .post<MedicalOrderExtracted>(this.extractProceduresEndPoint, params)
      .toPromise();
  }
}
